import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SectionHeader from "@/components/SectionHeader";
import { Bike } from "lucide-react";

/**
 * Classic biker "zakładka" column: 1 motocykl na rząd,
 * naprzemiennie przesunięty lewo / prawo.
 *    .  Adam
 *     . Bartek
 *    .  Czarek
 *     . Damian
 */
function orderItems(items) {
  const arr = (items || []).map((x, i) => ({
    name: x.name || "",
    motorcycle: x.motorcycle || "",
    _row: Number.isFinite(x.row) ? x.row : 0,
    _col: Number.isFinite(x.col) ? x.col : i,
    _idx: i,
  }));
  const hasPos = arr.some((x) => x._row > 0 || x._col > 0);
  if (hasPos) arr.sort((a, b) => a._row - b._row || a._col - b._col || a._idx - b._idx);
  return arr.map(({ name, motorcycle }) => ({ name, motorcycle }));
}

function FormationView({ formation }) {
  const ordered = orderItems(formation.items);
  return (
    <div
      className="sr-card p-5 md:p-8"
      style={{ background: "linear-gradient(180deg, rgba(12,10,9,0.9), rgba(12,10,9,0.7))" }}
    >
      <div className="flex flex-col gap-2 items-start mx-auto" style={{ width: "fit-content", minWidth: 260 }}>
        {ordered.map((it, i) => {
          const offset = i % 2 === 1;
          return (
            <div
              key={i}
              className="flex items-center gap-3"
              style={{ marginLeft: offset ? 80 : 0 }}
              data-testid={`formation-slot-${i}`}
            >
              <div
                className="flex items-center justify-center rounded-full shrink-0"
                style={{
                  width: 42,
                  height: 42,
                  background: "rgba(255,106,26,0.12)",
                  border: "2px solid var(--sr-fire)",
                  boxShadow: "0 0 14px rgba(255,106,26,0.25)",
                }}
              >
                <Bike className="text-[var(--sr-fire)]" size={22} />
              </div>
              <div>
                <p className="font-display text-sm text-[var(--sr-cream)] leading-tight">{it.name || "—"}</p>
                {it.motorcycle && (
                  <p className="text-[10px] font-nav uppercase tracking-widest" style={{ color: "var(--sr-muted)" }}>
                    {it.motorcycle}
                  </p>
                )}
              </div>
            </div>
          );
        })}
        {ordered.length === 0 && (
          <p className="text-xs italic" style={{ color: "var(--sr-muted)" }}>
            Szyk pusty
          </p>
        )}
      </div>
    </div>
  );
}

export default function Formation() {
  const [formations, setFormations] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/formations").then((r) => Array.isArray(r.data) && setFormations(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="formation-locked">
        <SectionHeader title="Szyk jazdy" />
        <p className="mt-6" style={{ color: "var(--sr-muted)" }}>
          Zaloguj się, aby zobaczyć szyk jazdy.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 py-12" data-testid="formation-page">
      <SectionHeader title="Szyk jazdy" />
      <p className="text-center mt-4 mb-10" style={{ color: "var(--sr-cream-soft)" }}>
        Układy kolumn jazdy dla nadchodzących wyjazdów i zlotów.
      </p>

      <div className="space-y-12">
        {formations.map((f) => (
          <div key={f.id} data-testid={`formation-${f.id}`}>
            <h3 className="font-display text-2xl text-[var(--sr-fire-2)] mb-1">{f.title}</h3>
            {f.description && (
              <p className="text-sm mb-5" style={{ color: "var(--sr-cream-soft)" }}>
                {f.description}
              </p>
            )}
            <FormationView formation={f} />
          </div>
        ))}
        {formations.length === 0 && (
          <p className="text-center" style={{ color: "var(--sr-muted)" }}>
            Brak układów. Poproś admina o utworzenie.
          </p>
        )}
      </div>
    </div>
  );
}
